import React, { useState } from 'react';
import {
  Row,
  Col,
  Form,
  Button,
  Table,
  Alert,
  ListGroup,
  Pagination,
  Container,
} from 'react-bootstrap';
import { useGetTransfersQuery, useUpdateTransferMutation } from '../../slices/transfersApiSlice';
import { useGetProductsQuery } from '../../slices/productsApiSlice';
import Loader from '../../components/Loader';
import Message from '../../components/Message';
import { toast } from 'react-toastify';

const TransfersSearchReplaceScreen = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [replacementName, setReplacementName] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [previewResults, setPreviewResults] = useState([]);
  const [isReplacing, setIsReplacing] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [focusedBox, setFocusedBox] = useState('search');

  const { data: transfersData, isLoading, error } = useGetTransfersQuery();
  const {
    data: productsData = { products: [], pages: 1 },
    isLoading: loadingProducts,
    error: errorProducts,
  } = useGetProductsQuery({ pageNumber });

  const [updateTransfer] = useUpdateTransferMutation();

  const handleProductSelect = (product) => {
    if (focusedBox === 'search') {
      setSearchTerm(product.name);
    } else {
      setReplacementName(product.name);
      setSelectedProduct(product);
    }
    setPreviewResults([]);
  };

  const handleSearch = () => {
    if (!searchTerm.trim()) return;

    const matches = transfersData?.transfers?.flatMap((transfer) =>
      transfer.transferItems
        .filter((item) => item.name.toLowerCase().includes(searchTerm.toLowerCase()))
        .map((item) => ({
          transferId: transfer._id,
          originalName: item.name,
          qty: item.qty,
          price: item.price,
          date: transfer.date,
        }))
    );

    setPreviewResults(matches || []);
  };

  const handleReplace = async () => {
    if (!replacementName.trim()) return toast.error('Replacement name required');
    if (!searchTerm.trim()) return toast.error('Search term required');

    const replacementProduct =
      productsData.products.find(
        (p) => p.name.toLowerCase() === replacementName.toLowerCase()
      ) ||
      productsData.products.find((p) =>
        p.name.toLowerCase().includes(replacementName.toLowerCase())
      );

    if (!replacementProduct) {
      const suggestions = productsData.products
        .filter((p) =>
          p.name.toLowerCase().includes(replacementName.toLowerCase())
        )
        .slice(0, 5);

      if (suggestions.length > 0) {
        toast.info(
          `No exact match. Did you mean: ${suggestions.map((p) => p.name).join(', ')}?`
        );
      } else {
        toast.error(`Product "${replacementName}" not found`);
      }
      return;
    }

    const affectedTransfers = transfersData?.transfers?.filter((transfer) =>
      transfer.transferItems.some((item) =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );

    if (!affectedTransfers || affectedTransfers.length === 0) {
      return toast.info('No matching transfers found');
    }

    setIsReplacing(true);

    try {
      for (const transfer of affectedTransfers) {
        const updatedItems = transfer.transferItems.map((item) =>
          item.name.toLowerCase().includes(searchTerm.toLowerCase())
            ? {
                ...item,
                name: replacementProduct.name,
                product: replacementProduct._id,
                price: replacementProduct.price,
                image: replacementProduct.image,
              }
            : item
        );

        await updateTransfer({
          transferId: transfer._id,
          updatedTransfer: {
            transferItems: updatedItems,
            customerDetails: transfer.customerDetails,
            note: transfer.note,
            status: transfer.status,
            date: transfer.date,
            images: transfer.images,
          },
        }).unwrap();
      }

      toast.success(`Replaced "${searchTerm}" with "${replacementProduct.name}"`);
      setPreviewResults([]);
      setSearchTerm('');
      setReplacementName('');
    } catch (err) {
      toast.error(err?.data?.message || 'Update failed');
    } finally {
      setIsReplacing(false);
    }
  };

  const activeFilter = focusedBox === 'search' ? searchTerm : replacementName;
  const filteredProducts = productsData.products.filter((p) =>
    p.name.toLowerCase().includes(activeFilter.toLowerCase())
  );

  return (
    <Container fluid>
      <Row>
        {/* Sidebar */}
        <Col md={3}>
          <h5 className="mb-3">Product List</h5>
          {loadingProducts ? (
            <Loader />
          ) : errorProducts ? (
            <Message variant="danger">
              {errorProducts?.data?.message || 'Failed to load products'}
            </Message>
          ) : (
            <>
              <ListGroup>
                {filteredProducts.map((product) => (
                  <ListGroup.Item
                    key={product._id}
                    action
                    onClick={() => handleProductSelect(product)}
                  >
                    {product.name || <em>No name</em>} — D
                    {product.price?.toFixed(2) || '0.00'}
                  </ListGroup.Item>
                ))}
              </ListGroup>
              <Pagination className="mt-3">
                {[...Array(productsData.pages).keys()].map((x) => (
                  <Pagination.Item
                    key={x + 1}
                    active={x + 1 === pageNumber}
                    onClick={() => setPageNumber(x + 1)}
                  >
                    {x + 1}
                  </Pagination.Item>
                ))}
              </Pagination>
            </>
          )}
        </Col>

        {/* Main Panel */}
        <Col md={9}>
          <Row className="mb-3">
            <Col><h1>Search & Replace Transfer Product Name</h1></Col>
          </Row>

          <Row className="mb-3">
            <Col md={4}>
              <Form.Control
                type="text"
                placeholder="Search for product name"
                value={searchTerm}
                onFocus={() => setFocusedBox('search')}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Col>
            <Col md={4}>
              <Form.Control
                type="text"
                placeholder="Replacement name"
                value={replacementName}
                onFocus={() => setFocusedBox('replace')}
                onChange={(e) => {
                  setReplacementName(e.target.value);
                  setSelectedProduct(null);
                }}
              />
            </Col>
            <Col md={4}>
              <Button
                variant="primary"
                onClick={handleSearch}
                className="me-2"
                disabled={!searchTerm.trim() || isReplacing}
              >
                Preview Matches
              </Button>
              <Button
                variant="success"
                onClick={handleReplace}
                disabled={!replacementName.trim() || isReplacing}
              >
                Replace All
              </Button>
            </Col>
          </Row>

          {selectedProduct && (
            <Alert variant="info">
              Selected replacement: <strong>{selectedProduct.name}</strong> — D
              {selectedProduct.price.toFixed(2)}
            </Alert>
          )}

          {isLoading ? (
            <Loader />
          ) : error ? (
            <Message variant="danger">{error?.data?.message || 'Error loading transfers'}</Message>
          ) : previewResults.length === 0 ? (
            <Alert variant="info">No matching items found. Try a different search term.</Alert>
          ) : (
            <Table striped bordered hover responsive className="table-sm">
              <thead>
                <tr>
                  <th>DATE</th>
                  <th>ORIGINAL NAME</th>
                  <th>QTY</th>
                  <th>PRICE</th>
                </tr>
              </thead>
              <tbody>
                {previewResults.map((item, idx) => (
                  <tr key={idx}>
                    <td>{new Date(item.date).toLocaleDateString()}</td>
                    <td>{item.originalName}</td>
                    <td>{item.qty}</td>
                    <td>D{item.price.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default TransfersSearchReplaceScreen;